/**
 * FMSynthNode - FM synthesis using Tone.js
 *
 * Endpoints:
 * - event (input): note events to play
 * - volume (input): output volume in dB
 * - octave (input): transposition in octaves (-4 to +4)
 * - harmonicity, modulationIndex (input): FM synth params
 * - envelope (input): full ADSR envelope
 * - attack, decay, sustain, release (input): envelope params
 *
 * Audio context is passed via dependency injection in initialize()
 */

import { ToneAudioBaseNode } from '../ToneAudioBaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { EnvelopeData } from '@nodes/EnvelopeData';
import { envelopeEndpoints } from '@audio/sharedEndpoints';
import { events, internals, params, audio } from '@/nodes/TypedEndpoints';
import { FM_SYNTH_PRESETS } from './presets';
import type { ToneJsDependencies } from '../../tonejsPlugin';

const endpoints = {
  ...events({
    event: { isInput: true, primary: true }
  }),
  ...audio({
    audioOut: { isOutput: true }
  }),
  ...internals({
    envelope: { dataType: 'envelope', isInput: true }
  }),
  ...params({
    preset: { dataType: 'string', options: FM_SYNTH_PRESETS.map(p => p.name) },
    volume: { value: -12, min: -60, max: 0 },
    defaultPitch: { value: 60, min: 0, max: 127, step: 1 },
    defaultLength: { dataType: 'string', value: '8n', options: ['1n', '2n', '4n', '8n', '16n', '32n'] as const },
    defaultVelocity: { value: 0.7, min: 0, max: 1, step: 0.01 },
    octave: { value: 0, min: -4, max: 4, step: 1 },
    harmonicity: { value: 3, min: 0.1, max: 20, step: 0.1 },
    modulationIndex: { name: 'Mod Index', value: 10, min: 0, max: 100 },
    ...envelopeEndpoints
  }),

};

export class FMSynthNode extends ToneAudioBaseNode<typeof endpoints> {
  private synth: any = null;

  constructor(id: string, deps: ToneJsDependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    this.synth = new this.tone.PolySynth(this.tone.FMSynth, {
      harmonicity: this.params.harmonicity.value,
      modulationIndex: this.params.modulationIndex.value,
      envelope: {
        attack: this.params.attack.value,
        decay: this.params.decay.value,
        sustain: this.params.sustain.value,
        release: this.params.release.value
      },
      modulationEnvelope: {
        attack: 0.5,
        decay: 0,
        sustain: 1,
        release: 0.5
      }
    });
    this.synth.volume.value = this.params.volume.value;

    // Connect synth to output endpoint
    this.audio.audioOut.connect(this.synth);
  }

  dispose(): void {
    if (this.synth) {
      this.synth.releaseAll();
      this.synth.dispose();
      this.synth = null;
    }
    super.dispose();
  }

  // Disable/enable individual ADSR endpoints when envelope is connected/disconnected
  private setEnvelopeEndpointsDisabled(disabled: boolean): void {
    for (const ep of ['attack', 'decay', 'sustain', 'release']) {
      this.setEndpointDisabled(ep, disabled);
    }
  }

  onConnectedFrom(endpointId: string): void {
    if (endpointId === 'envelope') {
      this.setEnvelopeEndpointsDisabled(true);
    }
  }

  onDisconnectedFrom(endpointId: string): void {
    if (endpointId === 'envelope') {
      this.setEnvelopeEndpointsDisabled(false);
    }
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    switch (endpointId) {
      case 'volume':
        if (this.synth) this.synth.volume.value = payload.value;
        break;
      case 'harmonicity':
        if (this.synth) this.synth.set({ harmonicity: payload.value });
        break;
      case 'modulationIndex':
        if (this.synth) this.synth.set({ modulationIndex: payload.value });
        break;
      case 'envelope':
        this.setFullEnvelope(payload.value);
        break;
      case 'attack':
      case 'decay':
      case 'sustain':
      case 'release':
        this.setEnvelope(endpointId, payload.value);
        break;
    }
  }

  private setEnvelope(param: string, value: number): void {
    if (this.synth) {
      const envelope = {
        attack: this.params.attack.value,
        decay: this.params.decay.value,
        sustain: this.params.sustain.value,
        release: this.params.release.value,
        [param]: value
      };
      this.synth.set({ envelope });
    }
  }

  private setFullEnvelope(envelope: EnvelopeData): void {
    if (!envelope || !this.synth) return;

    // Update store values
    if (envelope.attack !== undefined) this.params.attack.set(envelope.attack);
    if (envelope.decay !== undefined) this.params.decay.set(envelope.decay);
    if (envelope.sustain !== undefined) this.params.sustain.set(envelope.sustain);
    if (envelope.release !== undefined) this.params.release.set(envelope.release);

    this.synth.set({
      envelope: {
        attack: envelope.attack ?? 0.01,
        decay: envelope.decay ?? 0.1,
        sustain: envelope.sustain ?? 0.5,
        release: envelope.release ?? 0.5
      }
    });
  }

  handleEvent(payload: Payload, endpointId: string): void {
    // Duration: number = ticks, convert to Tone.js format; fallback to defaultLength
    const durationTicks = payload.duration;
    const duration = durationTicks != null ? `${durationTicks}i` : (this.params.defaultLength.value ?? '8n');
    const velocity = payload.velocity ?? this.params.defaultVelocity.value ?? 0.7;
    const time = payload.time ?? this.tone.now();

    // If value is a number, use it as MIDI note. Otherwise use defaultPitch parameter
    const baseNote = typeof payload.value === 'number' ? payload.value : (this.params.defaultPitch.value ?? 60);
    const midiNote = baseNote + (this.params.octave.value * 12);
    // Convert MIDI to note string - Tone.js interprets raw numbers as Hz frequency
    const note = this.tone.Frequency(midiNote, 'midi').toNote();

    this.synth.triggerAttackRelease(note, duration, time, velocity);
  }


}
// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const fmSynthNodeDef: NodeDef<FMSynthNode> = {
  type: 'FMSynth',
  shortName: 'fm',
  info: 'FM synthesizer using Tone.js. Harmonicity and modulation index control timbre complexity.',
  domain: Domain.Audio,
  endpoints,
  factoryPresets: FM_SYNTH_PRESETS,
  ctor: FMSynthNode
};
